import { Fragment, ReactNode } from 'react';

/**
 * Renders the published legal markdown (`site/auteurintelligence/content/legal/*.md`) as React
 * elements, for the in-app scroll box (F-6). It understands only what those documents use: front
 * matter (dropped), `#` headings, `-` lists, paragraphs, `**bold**` and `[text](url)` links. It never
 * injects HTML: every piece of text becomes a React text node.
 */

const SITE_ORIGIN = 'https://auteurintelligence.com';

function safeHref(target: string): string | null {
  if (target.startsWith('/') && !target.startsWith('//')) return SITE_ORIGIN + target;
  try {
    const url = new URL(target);
    return url.protocol === 'https:' || url.protocol === 'mailto:' ? url.toString() : null;
  } catch {
    return null;
  }
}

export function inline(text: string, keyPrefix = ''): ReactNode[] {
  const out: ReactNode[] = [];
  const pattern = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let index = 0;
  for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const key = `${keyPrefix}${index++}`;
    if (match[1] !== undefined) {
      out.push(<strong key={key}>{match[1]}</strong>);
    } else {
      const href = safeHref(match[3]);
      out.push(href
        ? <a key={key} href={href} target="_blank" rel="noopener noreferrer">{match[2]}</a>
        : <Fragment key={key}>{match[2]}</Fragment>);
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function stripFrontMatter(source: string): string {
  const normalized = source.replace(/\r\n/g, '\n');
  if (!normalized.startsWith('---\n')) return normalized;
  const end = normalized.indexOf('\n---\n', 4);
  return end === -1 ? normalized : normalized.slice(end + 5);
}

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'paragraph'; text: string };

export function legalBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: string[] | null = null;
  const flush = () => {
    if (paragraph.length) blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
    paragraph = [];
    if (list) blocks.push({ kind: 'list', items: list });
    list = null;
  };
  for (const raw of stripFrontMatter(source).split('\n')) {
    const line = raw.trimEnd();
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    const item = /^\s*(?:[-*]|\d+\.)\s+(.*)$/.exec(line);
    if (!line.trim()) {
      flush();
    } else if (heading) {
      flush();
      blocks.push({ kind: 'heading', level: heading[1].length, text: heading[2] });
    } else if (item) {
      if (paragraph.length) { blocks.push({ kind: 'paragraph', text: paragraph.join(' ') }); paragraph = []; }
      (list ??= []).push(item[1]);
    } else if (list && /^\s+/.test(raw)) {
      list[list.length - 1] += ' ' + line.trim();
    } else {
      if (list) { blocks.push({ kind: 'list', items: list }); list = null; }
      paragraph.push(line.trim());
    }
  }
  flush();
  return blocks;
}

/** One document, with its headings demoted below the scroll box's own document title. */
export function LegalText({ source, idPrefix }: { source: string; idPrefix: string }) {
  return (
    <>
      {legalBlocks(source).map((block, index) => {
        const key = `${idPrefix}-${index}`;
        if (block.kind === 'heading') {
          // The document's own "# Title" is shown by the caller as the section heading.
          if (block.level === 1) return null;
          const Tag = block.level === 2 ? 'h4' : 'h5';
          return <Tag key={key}>{inline(block.text, key)}</Tag>;
        }
        if (block.kind === 'list') {
          return <ul key={key}>{block.items.map((item, i) => <li key={`${key}-${i}`}>{inline(item, `${key}-${i}-`)}</li>)}</ul>;
        }
        return <p key={key}>{inline(block.text, key)}</p>;
      })}
    </>
  );
}
